import { cva, type VariantProps } from 'class-variance-authority';
import {
  controlChrome,
  disabledState,
  focusRing,
  invalidState,
  transitionBase,
  uiMarker,
} from './shared';

/**
 * TEXT-ENTRY CONTROLS
 *
 * Input, Textarea and the Select trigger all share this recipe so they cannot
 * drift apart. If you restyle the border, background or focus treatment here,
 * every control in every form moves together.
 */
export const inputRecipe = cva(
  [
    uiMarker,
    controlChrome,
    'font-sans text-body',
    'file:border-0 file:bg-transparent file:text-body-sm file:font-medium file:text-foreground',
    transitionBase,
    focusRing,
    invalidState,
    disabledState,
  ],
  {
    variants: {
      /**
       * `cell` is a value that sits flush inside something that already
       * draws the box — a rate in the pricing matrix, the count between a
       * stepper's minus and plus. No border, radius or shadow of its own,
       * centred, and it takes the cell's text colour so the container can
       * say "this one is adjusted". The focus ring is the whole selected
       * state, lifted above its neighbours so they do not clip it. A number
       * here never wants a spinner: it would push the value off centre.
       */
      variant: {
        default: '',
        /**
         * A heading that happens to be typeable — the section name over a
         * block of checklist questions. No box, no shadow, no side inset,
         * so it sits where a Text would; `size` still sets the type.
         */
        heading: 'rounded-none border-0 bg-transparent px-0 shadow-none',
        /**
         * No chrome and no ring: the box is drawn by what holds it, which
         * shows the focus instead — the comment pill at the foot of a photo,
         * whose send button sits inside the same outline.
         */
        bare: 'h-auto rounded-none border-0 bg-transparent px-0 shadow-none focus-visible:ring-0',
        cell: [
          'relative rounded-none border-0 bg-transparent shadow-none',
          'text-center text-current',
          'focus-visible:z-10',
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        ].join(' '),
      },
      size: {
        /*
         * The dense form shape: 32 high, 8px of side padding, 14px medium
         * text and the same 8px corner as the card it sits in.
         *
         * It is the editable twin of `readOnlyValueRecipe`'s `sm`, and that is
         * what fixes the numbers here. The checklist builder and the report
         * card put the two boxes in one grid, so a 13px value beside a 14px
         * one, or a 6px corner beside an 8px one, reads as a mistake rather
         * than as the difference between typeable and not.
         */
        sm: 'h-control-sm rounded-lg px-2 text-label',
        md: 'h-control-md px-3',
        lg: 'h-control-lg px-3.5',
        /** Pairs with Button's xl, so a full-page form's row lines up. */
        xl: 'h-control-xl px-4 text-body-lg',
      },
      /** Extra left padding to clear a leading icon rendered by the wrapper. */
      hasLeadingIcon: {
        true: '',
        false: '',
      },
      hasTrailingIcon: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // 8px of padding, a 16px glyph, then 4px before the text.
      { size: 'sm', hasLeadingIcon: true, class: 'pl-7' },
      { size: 'md', hasLeadingIcon: true, class: 'pl-9' },
      { size: 'lg', hasLeadingIcon: true, class: 'pl-10' },
      { size: 'xl', hasLeadingIcon: true, class: 'pl-11' },
      { size: 'sm', hasTrailingIcon: true, class: 'pr-7' },
      { size: 'md', hasTrailingIcon: true, class: 'pr-9' },
      { size: 'lg', hasTrailingIcon: true, class: 'pr-10' },
      { size: 'xl', hasTrailingIcon: true, class: 'pr-11' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hasLeadingIcon: false,
      hasTrailingIcon: false,
    },
  }
);

export type InputRecipeProps = VariantProps<typeof inputRecipe>;

/** Multi-line entry. Height comes from `rows`, so no fixed control height. */
export const textareaRecipe = cva(
  [
    uiMarker,
    controlChrome,
    'font-sans text-body px-3 py-2 min-h-control-lg resize-y',
    transitionBase,
    focusRing,
    invalidState,
    disabledState,
  ],
  {
    variants: {
      size: {
        sm: 'text-body-sm px-2.5 py-1.5',
        md: '',
        lg: 'px-3.5 py-2.5',
        // A textarea sizes by rows, so xl is padding and type only — it shares
        // the size union with Input, and a missing key silently renders md.
        xl: 'text-body-lg px-4 py-3',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/**
 * The icon slotted inside an input.
 *
 * Sizes the glyph as well as positioning it, the way Button does — before this
 * it only positioned, so a bare `leadingIcon={<Search />}` rendered at lucide's
 * 24px default inside a 36px control. The input's `pl-9` was always cut for a
 * 16px icon, so a 24px one ran right up to the text.
 *
 * Two spellings had grown up around that: `<Search />` (too big) and
 * `<Icon icon={Search} tone="muted" />` (correct, but only because the call
 * site knew to say so). Sizing it here makes both render the same.
 */
export const inputAdornmentRecipe = cva(
  [
    uiMarker,
    'pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground',
    '[&_svg]:shrink-0',
  ],
  {
    variants: {
      side: {
        leading: '',
        trailing: '',
      },
      size: {
        sm: '[&_svg]:size-icon-sm',
        md: '[&_svg]:size-icon-sm',
        lg: '[&_svg]:size-icon-md',
        xl: '[&_svg]:size-icon-md',
      },
    },
    compoundVariants: [
      { side: 'leading', size: 'sm', class: 'left-2' },
      { side: 'leading', size: 'md', class: 'left-3' },
      { side: 'leading', size: 'lg', class: 'left-3.5' },
      { side: 'leading', size: 'xl', class: 'left-4' },
      { side: 'trailing', size: 'sm', class: 'right-2' },
      { side: 'trailing', size: 'md', class: 'right-3' },
      { side: 'trailing', size: 'lg', class: 'right-3.5' },
      { side: 'trailing', size: 'xl', class: 'right-4' },
    ],
    defaultVariants: { side: 'leading', size: 'md' },
  }
);

/**
 * SELECT
 *
 * Shared by both of Select's shapes — the `options` array and the composed
 * children — so a select written one way is indistinguishable from one written
 * the other. That is the whole reason these live here rather than inline.
 */
export const selectTriggerRecipe = cva([
  uiMarker,
  'flex items-center justify-between gap-2 text-left [&>span]:truncate data-[placeholder]:text-muted-foreground',
]);

export const selectContentRecipe = cva([
  uiMarker,
  'relative z-50 max-h-96 min-w-[8rem] overflow-hidden',
  'rounded-md border border-border bg-popover text-popover-foreground shadow-e3',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  // Match the trigger's width so the list never looks detached from it.
  'w-[var(--radix-select-trigger-width)]',
]);

export const selectItemRecipe = cva([
  uiMarker,
  'relative flex w-full cursor-default select-none items-center',
  'rounded-sm py-1.5 pl-2 pr-8 font-sans text-body outline-none',
  'focus:bg-accent focus:text-accent-foreground',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
]);
